import isEqual from 'lodash/isEqual';
import {defaultInspectionPolicy, getInspectionPolicyError} from './InspectionPolicyControls';

const object = (value: any) => value !== null && typeof value === 'object' && !Array.isArray(value);
const own = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);
export type InspectionAuthority = {
  key: 'operationalControls' | 'operational_controls' | null;
  wrapperKnown: boolean;
  wrapper: any;
  present: boolean;
  value: any;
};

export function getInspectionAuthority(policy: any): InspectionAuthority {
  if (!object(policy)) return {key: null, wrapperKnown: false, wrapper: policy, present: false, value: undefined};
  const key = own(policy, 'operationalControls') ? 'operationalControls' : own(policy, 'operational_controls') ? 'operational_controls' : null;
  if (!key) return {key: null, wrapperKnown: true, wrapper: undefined, present: false, value: undefined};
  const wrapper = policy[key];
  if (!object(wrapper)) return {key, wrapperKnown: false, wrapper, present: false, value: undefined};
  return {key, wrapperKnown: true, wrapper, present: own(wrapper, 'inspection'), value: wrapper.inspection};
}

export function sameInspectionAuthority(actual: InspectionAuthority, expected: InspectionAuthority) {
  if (actual.wrapperKnown !== expected.wrapperKnown) return false;
  if (!expected.wrapperKnown) return actual.key === expected.key && isEqual(actual.wrapper, expected.wrapper);
  return actual.present === expected.present && (!expected.present || isEqual(actual.value, expected.value));
}

const shapeMatches = (value: any, shape: any): boolean => {
  if (Array.isArray(shape)) return Array.isArray(value);
  return shape && typeof shape === 'object'
    ? object(value) && isEqual(Object.keys(value).sort(), Object.keys(shape).sort()) && Object.keys(shape).every(key => shapeMatches(value[key], shape[key]))
    : typeof value === typeof shape;
};

export function hasCanonicalAllReturnsInspection(value: any): boolean {
  if (value?.enabled !== true || value?.mode !== 'ALWAYS' || !shapeMatches(value, defaultInspectionPolicy({enabled: true, mode: 'ALWAYS'}))) return false;
  const rules = value.rules;
  const number = (value: number, minimum: number, maximum: number, integer = false) => Number.isFinite(value) && value >= minimum && value <= maximum && (!integer || Number.isInteger(value));
  const selections = (values: any[], pattern?: RegExp, fold = false) => {
    const keys = values.map(value => typeof value === 'string' && fold ? value.toLowerCase() : value);
    return values.length <= 100 && new Set(keys).size === values.length && values.every(value => typeof value === 'string' && value.length > 0 && value.length <= 255 && value.trim() === value && !value.split('').some((character: string) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) && (!pattern || pattern.test(value)));
  };
  return selections(rules.products.productIds, /^gid:\/\/shopify\/Product\/[1-9]\d*$/) && selections(rules.products.collectionIds, /^gid:\/\/shopify\/Collection\/[1-9]\d*$/) &&
    selections(rules.products.tags, undefined, true) && selections(rules.reasons.reasonIds, undefined, true) &&
    (!rules.products.enabled || !!(rules.products.productIds.length + rules.products.collectionIds.length + rules.products.tags.length)) && (!rules.reasons.enabled || !!rules.reasons.reasonIds.length) &&
    number(rules.itemQuantity.minimum, 1, 10000, true) && number(rules.returnValue.minimum, 0, 1e9) && number(rules.customerHistory.minimumReturns, 1, 10000, true) && number(rules.customerHistory.lookbackDays, 1, 3650, true) && number(rules.risk.minimumScore, 0, 100);
}

export function completeInspectionPolicy(value: any): boolean {
  if (!object(value) || typeof value.enabled !== 'boolean' || (value.enabled ? !['ALWAYS', 'MATCHING'].includes(value.mode) : value.mode !== 'NEVER')) return false;
  return hasCanonicalAllReturnsInspection({...value, enabled: true, mode: 'ALWAYS'}) && getInspectionPolicyError(value) === null;
}

export function inspectionAuthorityVerified(authority: InspectionAuthority) {
  return authority.wrapperKnown && (!authority.present || (object(authority.value) && authority.value.enabled === false) || completeInspectionPolicy(authority.value));
}

// Called only after an explicit inspection choice/repair, never while hydrating a store.
export function copyKnownInspectionShape(value: any, shape: any): any {
  if (Array.isArray(shape)) return Array.isArray(value) ? [...value] : value;
  if (shape && typeof shape === 'object') return Object.fromEntries(Object.keys(shape).map(key => [key, copyKnownInspectionShape(value?.[key], shape[key])]));
  return value;
}
